import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AsyncState from "../components/AsyncState";
import ProductCard from "../components/ProductCard";
import { useLanguage } from "../context/LanguageContext";
import { productsApi } from "../api";

export default function HomePage() {
  const { language, pathFor, t } = useLanguage();
  const [categoryRows, setCategoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    productsApi.categories(language)
      .then((categories) => {
        const orderedCategories = [...categories].sort(
          (first, second) => (first.priority ?? 0) - (second.priority ?? 0),
        );

        return Promise.all(
          orderedCategories.map(async (category) => {
            try {
              const result = await productsApi.list(
                { page: 1, category__id: category.id },
                language,
              );
              return { category, products: result.results || [], error: false };
            } catch {
              return { category, products: [], error: true };
            }
          }),
        );
      })
      .then((rows) => active && setCategoryRows(rows))
      .catch(() => active && setError("We could not load the collection right now."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt, language]);

  useEffect(() => {
    document.title = "Eco Bambù · Prato, Italy";
  }, []);

  return (
    <>
      <section className="hero">
        <img
          src="/img/main_cover_2.jpg"
          alt="Eco Bambù showroom in Prato"
          fetchPriority="high"
        />
        <div className="hero-overlay">
          <h1>Natural materials,<br />made to be worn.</h1>
          <Link className="button-light" to={pathFor("/category/")}>{t("collection")}</Link>
        </div>
      </section>

      <AsyncState loading={loading} error={error} onRetry={() => setAttempt((value) => value + 1)}>
        <div className="home-category-sections">
          {categoryRows.map(({ category, products, error: rowError }, index) => (
            <section
              className={`home-category-section${index % 2 === 1 ? " section-muted" : ""}`}
              key={category.id}
              aria-labelledby={`category-${category.id}`}
            >
              <div className="container-wide">
                <div className="section-heading home-category-heading">
                  <div>
                    <h2 id={`category-${category.id}`}>{category.name}</h2>
                  </div>
                  <Link className="text-link" to={`${pathFor("/category/")}?category=${category.id}`}>
                    {t("viewAll")} →
                  </Link>
                </div>

                {products.length > 0 ? (
                  <div className="category-product-row" aria-label={`${category.name} products`}>
                    {products.map((product) => <ProductCard key={product.id} product={product} />)}
                  </div>
                ) : (
                  <div className="home-category-empty">
                    {rowError ? "This collection could not be loaded." : t("noProducts")}
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      </AsyncState>

      <section className="contact-cta">
        <h2>{t("moreInformation")}</h2>
        <Link className="button-dark" to={pathFor("/contact/")}>{t("talkToUs")}</Link>
      </section>
    </>
  );
}
